import React, { useState, useEffect } from 'react';
import {
  Show,
  TopToolbar,
  EditButton,
  DeleteButton,
  ListButton,
  useShowContext,
  useRedirect,
  useGetOne,
  useNotify,
  useRefresh,
} from 'react-admin';
import { BackButton } from '../../components/BackButton';
import { NavigationBreadcrumbs, BreadcrumbItem } from '../../components/NavigationBreadcrumbs';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Grid,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  TextField as MuiTextField,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from '@mui/material';
import {
  Edit as EditIcon,
  Email as EmailIcon,
  Print as PrintIcon,
  Visibility as PreviewIcon,
  Receipt as InvoiceIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Send as SendIcon,
  Payment as PaymentIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useBrand } from '../../components/ServerBrandProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

interface Customer {
  id: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  email?: string;
  phone?: string;
  emails?: Array<{
    id: string;
    email: string;
    isPrimary: boolean;
    customerId: string;
  }>;
}

interface LineItem {
  id?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
}

interface Payment {
  id?: string;
  paymentDate: string;
  amount: number;
  method: string;
  notes?: string;
}

interface InvoiceRecord {
  id: string;
  invoiceNumber: string;
  title?: string;
  customerId: string;
  customer?: Customer;
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  dueDate: string;
  createdAt: string;
  lineItems?: LineItem[];
  payments?: Payment[];
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  taxRate?: number;
  paidAmount: number;
  balanceAmount: number;
  notes?: string;
  terms?: string;
}

interface EmailPreviewData {
  preview: boolean;
  subject: string;
  html: string;
  templateData: {
    invoiceNumber: string;
    isInvoice: boolean;
    customer: {
      name: string;
      company?: string;
    };
    amount: number;
    formattedAmount: string;
    dueDate: string;
    notes?: string;
    customMessage?: string;
  };
  template: string;
}

interface EmailFormData {
  toEmail: string;
  ccEmails: string[];
  subject: string;
  message: string;
  includePdf: boolean;
}

interface PaymentFormData {
  amount: number | string;
  paymentDate: Date;
  method: string;
  notes: string;
}

// API Functions
const generateEmailPreview = async (
  invoiceId: string,
  emailData: Partial<EmailFormData>
): Promise<EmailPreviewData> => {
  const accessToken = localStorage.getItem('accessToken');
  const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

  const response = await fetch(`${baseUrl}/crm/invoices/${invoiceId}/email/preview`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'x-tenant-code': localStorage.getItem('currentTenant')
        ? JSON.parse(localStorage.getItem('currentTenant')!).code
        : 'default',
    },
    body: JSON.stringify({
      ...emailData,
      includePdf: true,
      previewOnly: true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to generate email preview');
  }

  return response.json();
};

const sendInvoiceEmail = async (
  invoiceId: string,
  emailData: EmailFormData
): Promise<{ sent: boolean; to: string; subject: string }> => {
  const accessToken = localStorage.getItem('accessToken');
  const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

  const response = await fetch(`${baseUrl}/crm/invoices/${invoiceId}/email`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'x-tenant-code': localStorage.getItem('currentTenant')
        ? JSON.parse(localStorage.getItem('currentTenant')!).code
        : 'default',
    },
    body: JSON.stringify({
      ...emailData,
      includePdf: true,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to send email');
  }

  return response.json();
};

const recordPayment = async (invoiceId: string, paymentData: PaymentFormData): Promise<Payment> => {
  const accessToken = localStorage.getItem('accessToken');
  const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

  const response = await fetch(`${baseUrl}/crm/invoices/${invoiceId}/payments`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
      'x-tenant-code': localStorage.getItem('currentTenant')
        ? JSON.parse(localStorage.getItem('currentTenant')!).code
        : 'default',
    },
    body: JSON.stringify({
      ...paymentData,
      amount: typeof paymentData.amount === 'string' ? parseFloat(paymentData.amount) : paymentData.amount,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to record payment');
  }

  return response.json();
};

const ShowActions = () => (
  <TopToolbar>
    <BackButton 
      fallbackPath="/invoices" 
      variant="icon" 
      tooltip="Back to invoices list"
    />
    <ListButton />
    <EditButton />
    <DeleteButton />
  </TopToolbar>
);

const StatusChip = ({ record }: { record: InvoiceRecord }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'sent':
        return 'info';
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={record.status?.toUpperCase() || 'UNKNOWN'}
      color={
        getStatusColor(record.status) as
          | 'default'
          | 'primary'
          | 'secondary'
          | 'success'
          | 'info'
          | 'warning'
          | 'error'
      }
      size="small"
    />
  );
};

const InvoiceShowContent = () => {
  const { record } = useShowContext<InvoiceRecord>();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const { brand } = useBrand();

  // Helper function to get customer's primary email
  const getCustomerEmail = (customer: Customer | undefined): string => {
    if (!customer?.emails || customer.emails.length === 0) {
      return customer?.email || ''; // Fallback to direct email field if it exists
    }

    // Look for primary email first
    const primaryEmail = customer.emails.find(email => email.isPrimary);
    if (primaryEmail) {
      return primaryEmail.email;
    }

    // If no primary email, use the first email
    return customer.emails[0]?.email || '';
  };
  const [previewOpen, setPreviewOpen] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState<EmailPreviewData | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);
  const [emailForm, setEmailForm] = useState<EmailFormData>({
    toEmail: '',
    ccEmails: [],
    subject: '',
    message: '',
    includePdf: true,
  });
  const [paymentForm, setPaymentForm] = useState<PaymentFormData>({
    amount: '',
    paymentDate: new Date(),
    method: 'cash',
    notes: '',
  });

  // Get customer details
  const { data: customer } = useGetOne<Customer>(
    'customers',
    { id: record?.customerId },
    { enabled: !!record?.customerId }
  );

  // Update email form when customer data becomes available
  useEffect(() => {
    const customerEmail = getCustomerEmail(customer);
    if (customerEmail && emailDialogOpen && !emailForm.toEmail) {
      setEmailForm(prev => ({
        ...prev,
        toEmail: customerEmail,
      }));
    }
  }, [customer, emailDialogOpen, emailForm.toEmail]);

  if (!record) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleEdit = () => {
    redirect('edit', 'invoices', record.id);
  };

  const handlePreview = async () => {
    if (!record?.id) {
      return;
    }

    setPreviewOpen(true);

    // Generate preview using the server API
    try {
      setIsLoadingPreview(true);
      const preview = await generateEmailPreview(record.id, {
        toEmail: 'temp@example.com', // Required but not used for preview
        includePdf: true,
      });
      setEmailPreviewData(preview);
    } catch (error) {
      notify('Failed to generate preview', { type: 'error' });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSend = async () => {
    if (!record?.id) {
      return;
    }

    // Initialize form with customer email and generate preview
    const customerEmail = getCustomerEmail(customer);
    const initialEmailData = {
      toEmail: customerEmail,
      ccEmails: [],
      subject: `Invoice ${record.invoiceNumber} from Your Company`,
      message: `Please find attached your invoice for ${
        record.title || 'services'
      }.\n\nThank you for your business!`,
      includePdf: true,
    };

    setEmailForm(initialEmailData);
    setEmailDialogOpen(true);

    // Show a helpful message if no customer email is available
    if (!customerEmail && customer) {
      notify(
        'Customer does not have an email address on file. Please enter an email address to send the invoice.',
        { type: 'info' }
      );
    } else if (!customer) {
      notify('Customer information is still loading. Please enter the email address manually.', {
        type: 'info',
      });
    }

    // Generate preview
    try {
      setIsLoadingPreview(true);
      const preview = await generateEmailPreview(record.id, initialEmailData);
      setEmailPreviewData(preview);
      setEmailForm(prev => ({
        ...prev,
        subject: preview.subject,
      }));
    } catch (error) {
      notify('Failed to generate email preview', { type: 'error' });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleRecordPayment = () => {
    setPaymentForm({
      amount: '',
      paymentDate: new Date(),
      method: 'cash',
      notes: '',
    });
    setRecordPaymentOpen(true);
  };

  const handlePreviewEmail = async () => {
    if (!record?.id) {
      return;
    }

    try {
      setIsLoadingPreview(true);
      const preview = await generateEmailPreview(record.id, emailForm);
      setEmailPreviewData(preview);
      setEmailForm(prev => ({
        ...prev,
        subject: preview.subject,
      }));
    } catch (error) {
      notify('Failed to generate email preview', { type: 'error' });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleSendEmail = async () => {
    if (!record?.id) {
      return;
    }

    try {
      setIsSendingEmail(true);
      const result = await sendInvoiceEmail(record.id, emailForm);

      if (result.sent) {
        notify(`Invoice sent successfully to ${result.to}`, { type: 'success' });
        setEmailDialogOpen(false);
        setEmailPreviewData(null);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send email';
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSavePayment = async () => {
    if (!record?.id) {
      return;
    }

    try {
      setIsRecordingPayment(true);
      await recordPayment(record.id, paymentForm);
      notify('Payment recorded successfully!', { type: 'success' });
      setRecordPaymentOpen(false);
      refresh(); // Refresh to show updated payment data
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to record payment';
      notify(errorMessage, { type: 'error' });
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const handleEmailFormChange = (
    field: keyof EmailFormData,
    value: string | string[] | boolean
  ) => {
    setEmailForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const addCcEmail = () => {
    setEmailForm(prev => ({
      ...prev,
      ccEmails: [...prev.ccEmails, ''],
    }));
  };

  const updateCcEmail = (index: number, value: string) => {
    setEmailForm(prev => ({
      ...prev,
      ccEmails: prev.ccEmails.map((email, i) => (i === index ? value : email)),
    }));
  };

  const removeCcEmail = (index: number) => {
    setEmailForm(prev => ({
      ...prev,
      ccEmails: prev.ccEmails.filter((_, i) => i !== index),
    }));
  };

  const formatCurrency = (amount: number | undefined | null) => {
    // Handle undefined, null, or NaN values
    const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(safeAmount);
  };

  const breadcrumbItems: BreadcrumbItem[] = [
    {
      label: 'Invoices',
      path: '/invoices',
      icon: <InvoiceIcon fontSize="small" />,
    },
    {
      label: `Invoice #${record.invoiceNumber}`,
      icon: <InvoiceIcon fontSize="small" />,
    },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <NavigationBreadcrumbs items={breadcrumbItems} />
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background:
            brand?.theme?.headerGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Stack direction="row" spacing={2} alignItems="center">
                <InvoiceIcon sx={{ fontSize: 40, color: 'white' }} />
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {record.title || 'Invoice'}
                  </Typography>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Invoice #{record.invoiceNumber}
                  </Typography>
                  <StatusChip record={record} />
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEdit}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={handlePreview}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  Preview
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={handleSend}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  Email
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PaymentIcon />}
                  onClick={handleRecordPayment}
                  sx={{ color: 'white', borderColor: 'white' }}
                >
                  Record Payment
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Customer Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: 'fit-content', mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <PersonIcon color="primary" />
                Customer Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {customer ? (
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {customer.displayName}
                    </Typography>
                    {customer.company && (
                      <Typography variant="body2" color="text.secondary">
                        {customer.company}
                      </Typography>
                    )}
                  </Box>
                  {(getCustomerEmail(customer) || customer.emails?.length) && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {customer.emails?.length > 1 ? 'Emails' : 'Email'}
                      </Typography>
                      {customer.emails?.length > 0 ? (
                        customer.emails.map(emailObj => (
                          <Typography
                            key={emailObj.id}
                            variant="body2"
                            sx={{
                              fontWeight: emailObj.isPrimary ? 'bold' : 'normal',
                              color: emailObj.isPrimary ? 'primary.main' : 'inherit',
                            }}
                          >
                            {emailObj.email} {emailObj.isPrimary && '(Primary)'}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2">{getCustomerEmail(customer)}</Typography>
                      )}
                    </Box>
                  )}
                  {customer.phone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">{customer.phone}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Due Date
                    </Typography>
                    <Typography variant="body2">
                      {record.dueDate ? new Date(record.dueDate).toLocaleDateString() : 'Not set'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Created
                    </Typography>
                    <Typography variant="body2">
                      {new Date(record.createdAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Loading customer information...
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card sx={{ height: 'fit-content' }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <MoneyIcon color="primary" />
                Financial Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Subtotal:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(record.subtotal)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2">Tax:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(record.taxAmount)}
                  </Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Total:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatCurrency(record.totalAmount)}
                  </Typography>
                </Box>
                <Divider />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="success.main">
                    Paid:
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatCurrency(record.paidAmount)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography
                    variant="body2"
                    color={(record.balanceAmount || 0) > 0 ? 'error.main' : 'success.main'}
                  >
                    Balance:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 'bold',
                      color: (record.balanceAmount || 0) > 0 ? 'error.main' : 'success.main',
                    }}
                  >
                    {formatCurrency(record.balanceAmount)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Line Items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <BusinessIcon color="primary" />
                Line Items
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 80 }}>Qty</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Unit Price</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {record.lineItems && record.lineItems.length > 0 ? (
                      record.lineItems.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {item.description}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.quantity || 0}</TableCell>
                          <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                          <TableCell>{formatCurrency(item.amount)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No line items found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Payment History */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <PaymentIcon color="primary" />
                Payment History
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 120 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: 100 }}>Method</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {record.payments && record.payments.length > 0 ? (
                      record.payments.map((payment, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {new Date(payment.paymentDate).toLocaleDateString()}
                          </TableCell>
                          <TableCell>{formatCurrency(payment.amount)}</TableCell>
                          <TableCell>{payment.method}</TableCell>
                          <TableCell>{payment.notes || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No payments recorded
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Additional Information */}
          {(record.notes || record.terms) && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={3}>
                  {record.notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Notes
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.notes}
                      </Typography>
                    </Grid>
                  )}
                  {record.terms && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        Terms & Conditions
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.terms}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Invoice Preview</Typography>
            <Button onClick={() => setPreviewOpen(false)} color="inherit" size="small">
              <CloseIcon />
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {isLoadingPreview ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
              }}
            >
              <Stack spacing={2} alignItems="center">
                <CircularProgress size={48} />
                <Typography>Generating preview...</Typography>
              </Stack>
            </Box>
          ) : emailPreviewData ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Invoice preview generated successfully. This shows how the document will appear
                  when printed or emailed.
                </Typography>
              </Alert>

              {/* HTML Preview Container */}
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: '70vh',
                  backgroundColor: 'white',
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: emailPreviewData.html }}
                  style={{
                    padding: '20px',
                    fontFamily: 'Arial, sans-serif',
                    lineHeight: '1.5',
                    color: '#333',
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Alert severity="error">
              <Typography variant="body2">
                Failed to generate preview. Please try again or contact support if the issue
                persists.
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {emailPreviewData && (
            <Button
              onClick={() => window.print()}
              variant="contained"
              startIcon={<PrintIcon />}
              sx={{
                background:
                  brand?.theme?.buttonGradient ||
                  'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                color: 'white',
              }}
            >
              Print
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog
        open={recordPaymentOpen}
        onClose={() => setRecordPaymentOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Record Payment</Typography>
            <Button onClick={() => setRecordPaymentOpen(false)} color="inherit" size="small">
              <CloseIcon />
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            <Alert severity="info">
              Record a payment for Invoice #{record.invoiceNumber}. Outstanding balance:{' '}
              {formatCurrency(record.balanceAmount)}
            </Alert>

            <MuiTextField
              fullWidth
              label="Payment Amount"
              type="number"
              value={paymentForm.amount}
              onChange={e => {
                const value = e.target.value;
                setPaymentForm(prev => ({ ...prev, amount: value === '' ? '' : parseFloat(value) || '' }));
              }}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={`Outstanding balance: ${formatCurrency(record.balanceAmount)}`}
            />

            <DatePicker
              label="Payment Date"
              value={paymentForm.paymentDate}
              onChange={date => date && setPaymentForm(prev => ({ ...prev, paymentDate: date }))}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />

            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentForm.method}
                label="Payment Method"
                onChange={e => setPaymentForm(prev => ({ ...prev, method: e.target.value }))}
              >
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="check">Check</MenuItem>
                <MenuItem value="credit_card">Credit Card</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                <MenuItem value="paypal">PayPal</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>

            <MuiTextField
              fullWidth
              label="Notes (Optional)"
              multiline
              rows={3}
              value={paymentForm.notes}
              onChange={e => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Payment reference, check number, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordPaymentOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSavePayment}
            variant="contained"
                          disabled={isRecordingPayment || !paymentForm.amount || parseFloat(paymentForm.amount.toString()) <= 0}
            startIcon={isRecordingPayment ? <CircularProgress size={16} /> : <PaymentIcon />}
            sx={{
              background:
                brand?.theme?.buttonGradient || 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              color: 'white',
            }}
          >
            {isRecordingPayment ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Send Invoice via Email</Typography>
            <Button onClick={() => setEmailDialogOpen(false)} color="inherit" size="small">
              <CloseIcon />
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Email Form */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Email Details
                </Typography>

                {/* To Email */}
                <MuiTextField
                  fullWidth
                  label="To Email"
                  type="email"
                  value={emailForm.toEmail}
                  onChange={e => handleEmailFormChange('toEmail', e.target.value)}
                  required
                  sx={{ mb: 2 }}
                  error={!emailForm.toEmail}
                  helperText={!emailForm.toEmail ? 'Email address is required' : ''}
                />

                {/* CC Emails */}
                <Box sx={{ mb: 2 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="subtitle2">CC Emails (Optional)</Typography>
                    <Button size="small" onClick={addCcEmail}>
                      Add CC
                    </Button>
                  </Stack>
                  {emailForm.ccEmails.map((email, index) => (
                    <Stack key={index} direction="row" spacing={1} sx={{ mb: 1 }}>
                      <MuiTextField
                        fullWidth
                        size="small"
                        type="email"
                        placeholder="cc@example.com"
                        value={email}
                        onChange={e => updateCcEmail(index, e.target.value)}
                      />
                      <Button size="small" color="error" onClick={() => removeCcEmail(index)}>
                        Remove
                      </Button>
                    </Stack>
                  ))}
                </Box>

                {/* Subject */}
                <MuiTextField
                  fullWidth
                  label="Subject"
                  value={emailForm.subject}
                  onChange={e => handleEmailFormChange('subject', e.target.value)}
                  sx={{ mb: 2 }}
                />

                {/* Message */}
                <MuiTextField
                  fullWidth
                  label="Message"
                  multiline
                  rows={4}
                  value={emailForm.message}
                  onChange={e => handleEmailFormChange('message', e.target.value)}
                  placeholder="Enter your message here..."
                  sx={{ mb: 2 }}
                />

                {/* Include PDF */}
                <FormControl>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2">Include PDF Attachment:</Typography>
                    <Chip
                      label={emailForm.includePdf ? 'Yes' : 'No'}
                      color={emailForm.includePdf ? 'success' : 'default'}
                      size="small"
                    />
                  </Stack>
                </FormControl>
              </CardContent>
            </Card>

            {/* Email Preview */}
            {emailPreviewData && (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Email Preview
                  </Typography>
                  {isLoadingPreview ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Subject: {emailPreviewData.subject}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        To: {emailForm.toEmail}
                      </Typography>
                      {emailForm.ccEmails.length > 0 && (
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          CC: {emailForm.ccEmails.filter(email => email.trim()).join(', ')}
                        </Typography>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                        {emailForm.message}
                      </Typography>
                      {emailPreviewData.templateData.customMessage && (
                        <Typography variant="body2" color="text.secondary">
                          {emailPreviewData.templateData.customMessage}
                        </Typography>
                      )}
                      <Alert severity="info" sx={{ mt: 2 }}>
                        PDF attachment will be included:{' '}
                        {emailPreviewData.templateData.invoiceNumber}
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Error Handling */}
            {!emailPreviewData && !isLoadingPreview && emailForm.toEmail && (
              <Alert severity="info">
                Click &quot;Preview Email&quot; to see how the email will look before sending.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handlePreviewEmail}
            disabled={!emailForm.toEmail || isLoadingPreview}
            startIcon={isLoadingPreview ? <CircularProgress size={16} /> : <PreviewIcon />}
          >
            Preview Email
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            disabled={!emailForm.toEmail || !emailPreviewData || isSendingEmail}
            startIcon={isSendingEmail ? <CircularProgress size={16} /> : <SendIcon />}
            sx={{
              background:
                brand?.theme?.buttonGradient || 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
              color: 'white',
            }}
          >
            Send Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const InvoiceShow = () => (
  <Show actions={<ShowActions />}>
    <InvoiceShowContent />
  </Show>
);
